import { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance.js";
import Pagination from "../../common/Pagination/index.js";
import Loader from "../../common/Loader/index.js";
import { BiSolidEditAlt } from "react-icons/bi";
import { MdClose, MdDeleteForever } from "react-icons/md";
import Model from "../../common/Model/index.js";
import { LiaSearchPlusSolid } from "react-icons/lia";
import { notify, Toast } from "../../common/Toast/index.js";
import FormFields from "../../common/FormFields/index.js";
import { getUserMenuPermission } from "../../common/Helper/index.js";

const Item = () => {
  const permission = getUserMenuPermission(4);
  const [isItemData, setIsItemData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [isEditing, setIsEditing] = useState(false);
  const [isItemType, setIsItemType] = useState([]);
  const [isCoilData, setIsCoilData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    item_Code: 0,
    item_Name: "",
    description: "",
    item_Rate: "",
    item_Type: "",
    item_Face: "",
    coil_Used: "",
    thickness: "",
    image: null,
  });
  const userId = sessionStorage.getItem("userId");

  const fetchItemType = async () => {
    if (isItemType?.length > 0) return;
    try {
      const res = await axiosInstance.get(`Item/GetAllItemType`);
      setIsItemType(res.data);
    } catch (error) {
      console.error("Error fetching GetAllItemType:", error);
    }
  };
  const fetchCoilData = async () => {
    if (isCoilData?.length > 0) return;
    try {
      const res = await axiosInstance.get(`Item/GetCoilMaster`);
      setIsCoilData(res.data);
    } catch (error) {
      console.error("Error fetching GetCoilMaster:", error);
    }
  };

  const fetchItemData = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.post(
        `Item/GetAll/?pageNumber=${currentPage}&pageSize=${pageSize}`,
        {
          pageNumber: currentPage,
          pageSize,
          item_Code: 0,
        }
      );
      setIsItemData(res.data.items);
      setFilteredData(res.data.items);
      setTotalRecords(res.data.totalRecords);
      setTotalPages(
        res.data.items?.length < pageSize ? currentPage : currentPage + 1
      );
    } catch (err) {
      notify("Error fetching data:", "error");
    } finally {
      setLoading(false);
    }
  };
  const fields = [
    {
      name: "item_Name",
      label: "Item Name",
      type: "text",
      required: true,
      errorMessage: "Item name is required",
    },
    { name: "description", label: "Description", type: "text" },
    {
      name: "item_Rate",
      label: "Item Rate",
      type: "number",
      required: true,
      min: 0,
      step: "any",
      errorMessage: "Positive value required",
    },
    { name: "item_Face", label: "Item Face", type: "number" },
    {
      name: "item_Type",
      label: "Item Type",
      type: "select",
      id: "item_Type",
      options: isItemType.map((type) => ({
        value: type.item_Type.toString(),
        label: type.item_Type_Name,
      })),
    },
    {
      name: "coil_Used",
      label: "Coil Used",
      type: "select",
      id: "coil_Used",
      options: isCoilData.map((coil) => ({
        value: coil.coil_Used.toString(),
        label: coil.coil_Name,
      })),
    },
    { name: "thickness", label: "Thickness", type: "number" },
    { name: "image", label: "Upload Image", type: "file" },
  ];

  const handleInputChange = (e) => {
    const { name, type, files, value } = e.target;

    if (type === "file") {
      const file = files[0];
      setFormData((prev) => ({
        ...prev,
        [name]: file,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleAddItem = () => {
    if (!permission.isInsert) {
      notify("You don't have permission to add Item.", "error");
      return;
    }
    setFormData({
      item_Code: "",
      item_Name: "",
      description: "",
      item_Rate: "",
      item_Type: "",
      item_Face: "",
      coil_Used: "",
      thickness: "",
    });
    setIsEditing(false);
    setIsModelOpen(true);
  };

  const handleEditItem = async (id) => {
    try {
      const { data } = await axiosInstance.get(`Item/GetById/${id}`);
      if (data) {
        setFormData({
          item_Code: data.item_Code || "",
          item_Name: data.item_Name || "",
          description: data.description || "",
          item_Rate: data.item_Rate || "",
          item_Type: data.item_Type || "",
          item_Face: data.item_Face || "",
          coil_Used: data.coil_Used || "",
          thickness: data.thickness || "",
          image: data.image || "",
        });
        setIsEditing(true);
        setIsModelOpen(true);
      }
    } catch (err) {
      notify("Error fetching Item data:", err);
    }
  };

  const handleDeleteItem = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axiosInstance.delete(`Item/Delete/${selectedId}`);
      fetchItemData();
      notify("Item deleted successfully!", "error");
    } catch (err) {
      notify("Failed to delete Item!", "error");
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing && !permission.isInsert) return;
    if (isEditing && !permission.isUpdate) return;
    const formDataToSend = new FormData();
    formDataToSend.append("Item_Code", formData.item_Code || 0);
    formDataToSend.append("Item_Name", formData.item_Name);
    formDataToSend.append("Description", formData.description);
    formDataToSend.append("Item_Rate", formData.item_Rate);
    formDataToSend.append("Item_Type", parseInt(formData.item_Type) || 0);
    formDataToSend.append("Item_Face", Number(formData.item_Face));
    formDataToSend.append("Coil_Used", parseInt(formData.coil_Used) || 0);
    formDataToSend.append("Thickness", Number(formData.thickness));

    if (formData.image) {
      formDataToSend.append("image", formData.image);
    }
    if (isEditing) {
      formDataToSend.append("Updated_By", userId);
    } else {
      formDataToSend.append("Created_By", userId);
    }
    try {
      await axiosInstance.post("Item/SaveUpdate", formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      fetchItemData();
      setIsModelOpen(false);
      notify(
        isEditing ? "Item updated successfully!" : "Item added successfully!",
        "success"
      );
      setFormData({
        item_Code: "",
        item_Name: "",
        description: "",
        item_Rate: "",
        item_Type: "",
        item_Face: "",
        coil_Used: "",
        thickness: "",
        image: null,
      });

      document.getElementById("fileInput").value = "";
    } catch (err) {
      console.error("Error submitting form:", err);
    }
  };

  const fetchFilteredItems = async (query) => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `Item/FilterByName?itemName=${query}`
      );
      setFilteredData(res.data);
      setTotalRecords(res.data.length);
    } catch (err) {
      notify("Error fetching filtered data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItemData();
    fetchItemType();
    fetchCoilData();
  }, [currentPage]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      fetchItemData();
    } else {
      fetchFilteredItems(searchQuery);
    }
  }, [searchQuery, currentPage]);

  const clearSearch = () => {
    setSearchQuery("");
    fetchItemData();
  };

  return (
    <>
      <Toast />
      <div className="rounded-md bg-white shadow-md border px-5 pt-6 pb-2.5">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
          <div className="relative w-full sm:w-1/2 lg:w-1/3">
            <input
              type="text"
              className="w-full bg-gray-100 border border-gray-3 py-2 pl-10 pr-10 rounded-md focus:ring-1 focus:ring-primary focus:border-primary outline-none"
              placeholder="Search ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary">
              <LiaSearchPlusSolid className="w-5 h-5" />
            </button>
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-danger"
              >
                <MdClose className="w-5 h-5" />
              </button>
            )}
          </div>
          {permission.isInsert && (
            <button
              className="bg-primary rounded px-4 text-white py-2"
              onClick={handleAddItem}
            >
              + Add Item
            </button>
          )}
        </div>

        {loading ? (
          <Loader />
        ) : permission.isView ? (
          <div className="overflow-x-auto rounded-lg">
            <table className="w-full min-w-[700px] text-sm text-left border border-gray rounded-lg">
              <thead className="bg-primary text-white tracking-widest text-center font-light">
                <tr>
                  <th className="border border-gray-400 px-4 py-3 whitespace-nowrap">
                    Image
                  </th>
                  <th className="border border-gray-400 px-4 py-3 whitespace-nowrap">
                    Item Name
                  </th>
                  <th className="border border-gray-400 px-4 py-3 whitespace-nowrap">
                    Description
                  </th>
                  <th className="border border-gray-400 px-4 py-3 whitespace-nowrap">
                    Item Rate
                  </th>
                  <th className="border border-gray-400 px-4 py-3 whitespace-nowrap">
                    Item Type
                  </th>
                  <th className="border border-gray-400 px-4 py-3 whitespace-nowrap">
                    Item Face
                  </th>
                  <th className="border border-gray-400 px-4 py-3 whitespace-nowrap">
                    Coil Used
                  </th>
                  <th className="border border-gray-400 px-4 py-3 whitespace-nowrap">
                    Thickness
                  </th>
                  <th
                    className={`${
                      !permission.isUpdate && !permission.isDelete
                        ? "hidden"
                        : ""
                    } border border-gray-400 px-4 py-3 whitespace-nowrap`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredData?.length > 0 ? (
                  filteredData.map((item) => (
                    <tr
                      key={item.item_Code}
                      className="border-b hover:bg-gray-50 transition cursor-pointer"
                    >
                      <td className="border border-gray-3 px-4 py-2 text-center">
                        {item.image ? (
                          <img
                            src={item.image}
                            alt="Item"
                            className="w-12 h-10 object-fill rounded-md"
                          />
                        ) : (
                          <span className="text-gray-500 italic tracking-wider">
                            No Image
                          </span>
                        )}
                      </td>
                      <td className="border border-gray-100 px-4 py-2 ">
                        {item.item_Name}
                      </td>
                      <td className="border border-gray-100 px-4 py-2 max-w-[200px]">
                        {item.description}
                      </td>
                      <td className="border border-gray-100 px-4 py-2">
                        {item.item_Rate}
                      </td>
                      <td className="border border-gray-100 px-4 py-2">
                        {isItemType.find(
                          (type) => type.item_Type === item.item_Type
                        )?.item_Type_Name || "N/A"}
                      </td>
                      <td className="border border-gray-100 px-4 py-2">
                        {item.item_Face}
                      </td>
                      <td className="border border-gray-100 px-4 py-2">
                        {isCoilData.find(
                          (coil) => coil.coil_Used === item.coil_Used
                        )?.coil_Name || "N/A"}
                      </td>
                      <td className="border border-gray-100 px-4 py-2">
                        {item.thickness}
                      </td>

                      {(permission.isUpdate || permission.isDelete) && (
                        <td className="py-3 border border-gray-100 text-center space-x-2">
                          {permission.isDelete && (
                            <button
                              className="bg-red-100 text-red-600 hover:bg-red-200 px-2 py-1 rounded-md"
                              onClick={() => handleDeleteItem(item.item_Code)}
                            >
                              <MdDeleteForever className="w-5 h-5" />
                            </button>
                          )}
                          {permission.isUpdate && (
                            <button
                              className="bg-[#e3f2fd] text-primary hover:bg-blue-200 px-2 py-1 rounded-md"
                              onClick={() => handleEditItem(item.item_Code)}
                            >
                              <BiSolidEditAlt className="w-5 h-5" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-red-500 text-lg py-10">
            You don't have permission to view this page.
          </div>
        )}

        <div className="flex justify-between items-center ">
          <p className="text-sm text-gray-6">
            Showing {isItemData?.length + (currentPage - 1) * 10} of{" "}
            {totalRecords} records
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
      <Model
        isOpen={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        name={isEditing ? "Edit Item" : "Add Item"}
      >
        <FormFields
          onSubmit={handleSubmit}
          setIsModelOpen={setIsModelOpen}
          formData={formData}
          handleInputChange={handleInputChange}
          fields={fields}
          onClear={() => setIsModelOpen(false)}
          isEditing={isEditing}
        />
      </Model>
      <Model
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <div className="p-4 text-center">
          <p className="text-lg font-semibold mb-4">
            Are you sure you want to delete this site?
          </p>
          <div className="flex justify-center gap-4">
            <button
              className="bg-gray-5 px-4 py-2 rounded-md"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="bg-danger text-white px-4 py-2 rounded-md"
              onClick={handleConfirmDelete}
            >
              Delete
            </button>
          </div>
        </div>
      </Model>
    </>
  );
};

export default Item;
